import { UserModulePermissions } from '../types/user';

// Központi jogosultság konfigurációk
export const DEFAULT_PERMISSIONS: Record<string, UserModulePermissions> = {
  customer: {
    customers: 'none',
    suppliers: 'none',
    products: 'none',
    inventory: 'none',
    quotations: 'none',
    deals: 'none',
    sales: 'none',
    contracts: 'none',
    templates: 'none',
    forms: 'none',
    worksheets: 'none',
    billing: 'none',
    reports: 'none',
    settings: 'access',
    admin: 'none',
    tasks: 'none',
    projects: 'none',
    notes: 'none',
    appointments: 'none',
    customerPortal: 'access'
  },
  super_admin: {
    customers: 'admin',
    suppliers: 'admin',
    products: 'admin',
    inventory: 'admin',
    quotations: 'admin',
    deals: 'admin',
    sales: 'admin',
    contracts: 'admin',
    templates: 'admin',
    forms: 'admin',
    worksheets: 'admin',
    billing: 'admin',
    reports: 'admin',
    settings: 'admin',
    admin: 'admin',
    tasks: 'admin',
    projects: 'admin',
    notes: 'admin',
    appointments: 'admin',
    customerPortal: 'admin'
  },
  admin: {
    customers: 'admin',
    suppliers: 'admin',
    products: 'admin',
    inventory: 'admin',
    quotations: 'admin',
    deals: 'admin',
    sales: 'admin',
    contracts: 'admin',
    templates: 'admin',
    forms: 'admin',
    worksheets: 'admin',
    billing: 'access',
    reports: 'access',
    settings: 'access',
    admin: 'none',
    tasks: 'admin',
    projects: 'admin',
    notes: 'admin',
    appointments: 'admin',
    customerPortal: 'admin'
  },
  user: {
    customers: 'access',
    suppliers: 'access',
    products: 'access',
    inventory: 'access',
    quotations: 'access',
    deals: 'access',
    sales: 'access',
    contracts: 'access',
    templates: 'access',
    forms: 'access',
    worksheets: 'access',
    billing: 'none',
    reports: 'none',
    settings: 'none',
    admin: 'none',
    tasks: 'access',
    projects: 'access',
    notes: 'access',
    appointments: 'access',
    customerPortal: 'access'
  }
};

// Üres jogosultságok (amikor nincs hozzáférés)
export const NO_PERMISSIONS: UserModulePermissions = {
  customers: 'none',
  suppliers: 'none',
  products: 'none',
  inventory: 'none',
  quotations: 'none',
  deals: 'none',
  sales: 'none',
  contracts: 'none',
  templates: 'none',
  forms: 'none',
  worksheets: 'none',
  billing: 'none',
  reports: 'none',
  settings: 'none',
  admin: 'none',
  tasks: 'none',
  projects: 'none',
  notes: 'none',
  appointments: 'none',
  customerPortal: 'none'
};

// Jogosultság szintek hierarchiája
export const PERMISSION_LEVELS = ['none', 'access', 'admin'] as const;

// Jogosultság szint ellenőrzés
export const hasPermissionLevel = (userLevel: string, requiredLevel: string): boolean => {
  const userLevelIndex = PERMISSION_LEVELS.indexOf(userLevel as any);
  const requiredLevelIndex = PERMISSION_LEVELS.indexOf(requiredLevel as any);
  
  return userLevelIndex >= requiredLevelIndex && userLevelIndex !== -1;
};

// Modul címkék (UI-ban való megjelenítéshez)
export const MODULE_LABELS: Record<keyof UserModulePermissions, string> = {
  customers: 'Ügyfelek',
  suppliers: 'Beszállítók',
  products: 'Termékek',
  inventory: 'Készletkezelés',
  quotations: 'Ajánlatok',
  deals: 'Üzletek',
  sales: 'Értékesítés',
  contracts: 'Szerződések',
  templates: 'Sablonok',
  forms: 'Űrlapok (Form Builder)',
  worksheets: 'Munkalapok',
  billing: 'Számlázás',
  reports: 'Jelentések',
  settings: 'Beállítások',
  admin: 'Rendszergazda',
  tasks: 'Feladatok',
  projects: 'Projektek',
  notes: 'Jegyzetek',
  appointments: 'Időpontfoglalás',
  customerPortal: 'Ügyfélportál'
};

// Jogosultság szint címkék
export const PERMISSION_LABELS = {
  none: 'Nincs hozzáférés',
  access: 'Hozzáférés',
  admin: 'Adminisztráció'
} as const;

// Jogosultság szint színek
export const PERMISSION_COLORS = {
  none: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-600',
  access: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700',
  admin: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
} as const;

// Modulok kategóriák szerint csoportosítva (UI megjelenítéshez)
export const MODULE_CATEGORIES = {
  core: {
    name: 'Alapfunkciók',
    modules: ['customers', 'settings'] as (keyof UserModulePermissions)[]
  },
  crm: {
    name: 'CRM',
    modules: ['suppliers', 'notes', 'appointments', 'customerPortal'] as (keyof UserModulePermissions)[]
  },
  products: {
    name: 'Termékek',
    modules: ['products', 'inventory'] as (keyof UserModulePermissions)[]
  },
  sales: {
    name: 'Értékesítés',
    modules: ['quotations', 'deals', 'sales'] as (keyof UserModulePermissions)[]
  },
  documents: {
    name: 'Dokumentumok',
    modules: ['contracts', 'templates', 'forms'] as (keyof UserModulePermissions)[]
  },
  projects: {
    name: 'Projektek',
    modules: ['projects', 'tasks', 'worksheets'] as (keyof UserModulePermissions)[]
  },
  finance: {
    name: 'Pénzügy',
    modules: ['billing', 'reports'] as (keyof UserModulePermissions)[]
  },
  admin: {
    name: 'Adminisztráció',
    modules: ['admin'] as (keyof UserModulePermissions)[]
  }
} as const; 